import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Phone, Calculator, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const RoofingCostsFAQPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Roofing Costs FAQ: How Much Does a New Roof Cost in NC? | Carolina Trophy Roofs</title>
        <meta name="description" content="Get detailed answers about roofing costs, pricing factors, and financing options for North Carolina homeowners. Expert cost guidance from Carolina Trophy Roofs." />
        <meta name="keywords" content="roof cost FAQ, roof replacement cost, roofing prices North Carolina, roof financing, roofing cost factors" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/roofing-costs-faq" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">Roofing Costs FAQ</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <DollarSign className="mr-2 h-4 w-4" />
              Cost & Pricing
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Roofing Costs FAQ: Understanding Your Investment
            </h1>
            <p className="text-xl text-gray-600">
              Transparent answers about roofing costs and financing options
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-8">
            {/* General Cost Questions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Calculator className="mr-2 h-6 w-6" />
                  General Cost Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">How much does a new roof cost in North Carolina?</h3>
                  <p className="text-gray-700 mb-3">
                    According to data from HomeAdvisor and Angie's List, roof replacement costs in North Carolina typically range:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Asphalt Shingles:</strong> $8,000 - $18,000 for average home (1,500-2,000 sq ft)</li>
                    <li><strong>Metal Roofing:</strong> $12,000 - $25,000</li>
                    <li><strong>Tile Roofing:</strong> $15,000 - $30,000</li>
                    <li><strong>Slate Roofing:</strong> $25,000 - $60,000+</li>
                  </ul>
                  <p className="text-gray-700">
                    These costs can vary significantly based on roof size, complexity, material quality, and local labor rates. 
                    The National Association of Home Builders notes that roofing costs have increased approximately 20% since 2020 
                    due to material and labor market changes.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What factors affect roofing costs the most?</h3>
                  <p className="text-gray-700 mb-3">
                    According to construction industry data from the Bureau of Labor Statistics and roofing trade associations, 
                    the primary cost factors include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Roof Size (40-50% of cost):</strong> Measured in squares (100 sq ft each)</li>
                    <li><strong>Material Choice (25-35%):</strong> From basic asphalt to premium materials</li>
                    <li><strong>Roof Complexity (10-20%):</strong> Slopes, angles, penetrations, and accessibility</li>
                    <li><strong>Labor Costs (20-30%):</strong> Vary by region and contractor expertise</li>
                    <li><strong>Tear-off and Disposal (5-10%):</strong> Removing old materials and cleanup</li>
                  </ul>
                  <p className="text-gray-700">
                    In North Carolina, hurricane-rated materials and proper installation for wind resistance can add 
                    10-15% to costs but provide significant long-term value.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">How much does roof repair cost compared to replacement?</h3>
                  <p className="text-gray-700 mb-3">
                    According to Thumbtack and HomeAdvisor cost data, common roof repairs in North Carolina cost:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Minor shingle repair:</strong> $150 - $400</li>
                    <li><strong>Flashing repair:</strong> $200 - $600</li>
                    <li><strong>Leak repair:</strong> $300 - $1,200</li>
                    <li><strong>Partial roof section:</strong> $1,000 - $4,000</li>
                  </ul>
                  <p className="text-gray-700">
                    The Consumer Reports "20% Rule" suggests that if repairs exceed 20% of replacement cost, 
                    replacement becomes more economical, especially for roofs over 15 years old.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Are expensive roofing materials worth the cost?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Remodeling Magazine "Cost vs. Value Report," roof replacement typically recoups 
                    60-70% of its cost in home value. Premium materials offer additional benefits:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Longer Lifespan:</strong> Metal and tile last 2-3x longer than basic asphalt</li>
                    <li><strong>Energy Savings:</strong> Cool roofs can reduce energy costs by 10-15% annually</li>
                    <li><strong>Weather Resistance:</strong> Higher wind ratings crucial for NC storm zones</li>
                    <li><strong>Insurance Discounts:</strong> Impact-resistant materials may qualify for premium reductions</li>
                  </ul>
                  <p className="text-gray-700">
                    The Department of Energy's analysis shows that energy-efficient roofing materials can provide 
                    a positive return on investment within 7-10 years in hot climates like North Carolina.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Insurance and Financing */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Insurance & Financing Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Will homeowners insurance cover my new roof?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Insurance Information Institute, homeowners insurance typically covers roof replacement 
                    when damage results from covered perils such as:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Hail damage</li>
                    <li>Wind damage (including hurricane damage)</li>
                    <li>Fire or lightning strikes</li>
                    <li>Falling objects (trees, debris)</li>
                  </ul>
                  <p className="text-gray-700 mb-3">
                    Insurance typically does NOT cover:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li>Normal wear and tear</li>
                    <li>Poor maintenance</li>
                    <li>Cosmetic damage</li>
                    <li>Damage from neglect</li>
                  </ul>
                  <p className="text-gray-700">
                    The National Association of Insurance Commissioners notes that most policies include a deductible 
                    (typically $500-$2,500) that you'll pay out of pocket.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">What financing options are available for roof replacement?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Federal Trade Commission and financial industry data, common roofing financing options include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Home Equity Loans:</strong> Typically 3-7% interest, tax-deductible interest</li>
                    <li><strong>Personal Loans:</strong> 5-15% interest, no collateral required</li>
                    <li><strong>Contractor Financing:</strong> Often 0% promotional rates for qualified buyers</li>
                    <li><strong>Credit Cards:</strong> Higher rates but potential for rewards/cash back</li>
                    <li><strong>FHA Title I Loans:</strong> Government-backed home improvement loans</li>
                  </ul>
                  <p className="text-gray-700">
                    Bankrate's analysis suggests that home equity loans typically offer the lowest rates for 
                    large home improvements like roofing projects.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Can I get tax credits for a new roof?</h3>
                  <p className="text-gray-700 mb-3">
                    According to the Internal Revenue Service and Department of Energy, certain roofing materials 
                    may qualify for federal tax credits:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Solar Roofing:</strong> 30% federal tax credit through 2032</li>
                    <li><strong>Metal Roofs with Energy Star rating:</strong> May qualify for energy efficiency credits</li>
                    <li><strong>Cool Roofs:</strong> Some utilities offer rebates for reflective roofing</li>
                  </ul>
                  <p className="text-gray-700">
                    Additionally, North Carolina offers various energy efficiency incentives through the 
                    NC Clean Energy Technology Center that may apply to certain roofing upgrades.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Hidden Costs and Budgeting */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <TrendingUp className="mr-2 h-6 w-6" />
                  Hidden Costs & Budgeting
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">What hidden costs should I budget for?</h3>
                  <p className="text-gray-700 mb-3">
                    According to contractor surveys by HomeAdvisor and construction industry data, 
                    common additional costs include:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Structural Repairs (15-25% of projects):</strong> $500-$3,000+</li>
                    <li><strong>Additional Ventilation:</strong> $300-$800</li>
                    <li><strong>Gutter Replacement:</strong> $800-$2,500</li>
                    <li><strong>Chimney Repairs:</strong> $200-$1,500</li>
                    <li><strong>Skylight Replacement:</strong> $400-$1,200 each</li>
                    <li><strong>Code Upgrades:</strong> $200-$1,000</li>
                  </ul>
                  <p className="text-gray-700">
                    Budget an additional 10-20% contingency for unexpected issues discovered during tearoff.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">When is the best time to replace a roof for cost savings?</h3>
                  <p className="text-gray-700 mb-3">
                    According to industry pricing data and contractor availability studies:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
                    <li><strong>Fall/Winter (Oct-Feb):</strong> 10-20% lower costs, better contractor availability</li>
                    <li><strong>Spring/Summer:</strong> Higher demand = higher prices, longer wait times</li>
                    <li><strong>Post-Storm Periods:</strong> Significantly higher costs due to demand surge</li>
                  </ul>
                  <p className="text-gray-700">
                    However, the National Weather Service data shows that planning ahead is crucial in North Carolina, 
                    where hurricane season runs June through November.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Authoritative Sources */}
          <Card className="mt-12">
            <CardHeader>
              <CardTitle>Authoritative Sources & References</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Cost & Market Data:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.homeadvisor.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        HomeAdvisor Cost Guides
                      </a>
                    </li>
                    <li>
                      <a href="https://www.angieslist.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Angie's List
                      </a>
                    </li>
                    <li>
                      <a href="https://www.remodeling.hw.net/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Remodeling Magazine Cost vs. Value
                      </a>
                    </li>
                    <li>
                      <a href="https://www.bls.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Bureau of Labor Statistics
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Financial & Government:</h4>
                  <ul className="space-y-1">
                    <li>
                      <a href="https://www.irs.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Internal Revenue Service
                      </a>
                    </li>
                    <li>
                      <a href="https://www.energy.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        U.S. Department of Energy
                      </a>
                    </li>
                    <li>
                      <a href="https://www.bankrate.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Bankrate
                      </a>
                    </li>
                    <li>
                      <a href="https://www.ftc.gov/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Federal Trade Commission
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <h2 className="text-2xl font-bold mb-4">Ready for a Free, No-Pressure Estimate?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Get transparent pricing and expert guidance for your roofing project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <a href="tel:252-371-2266">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (252) 371-2266
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Request Free Estimate</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RoofingCostsFAQPage;
